// @mui
import Card from '@mui/material/Card';
import { Box, Paper, TextField, Grid, Stack, Button, Typography, CircularProgress } from '@mui/material';
import CardContent from '@mui/material/CardContent';
// hooks
import { useEffect, useState, useRef } from 'react';
// components
import Iconify from 'src/components/iconify';
import { getRequest, postRequestForWebsite } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { handleErrorMessages } from 'src/services/fetching';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';
import useTimeManagementSection from 'src/hooks/use-time-management';

// ----------------------------------------------------------------------

export default function TimeManagement() {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const lastFeatureSectionRef = useRef(null);

    const timeManagementPageData = useSelector((state) => state.website?.timeManagementPage);
  const { fetchOnMount } = useTimeManagementSection();

  const [formData, setFormData] = useState({
    // Main Header
    mainTitle: "",
    mainTitleColor: "#000000",
    subtitle: "",
    subtitleColor: "#000000",

    // Main Description Paragraphs
    description1: "",
    description1Color: "#666666",

    description2: "",
    description2Color: "#666666",

    description3: "",
    description3Color: "#666666",

    description4: "",
    description4Color: "#666666",

    // Bottom CTA
    bottomCTA: "",
    bottomCTAColor: "#666666",
    bottomCTAStyle: "italic", // italic or normal

    // Feature Cards
    featureCards: [
      {
        title: "",
        iconUrl: "",
        backgroundColor: "#ffffff",
        textColor: "#000000"
      },
    ]
  });

  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (typeof current[key] !== 'object' || Array.isArray(current[key])) {
          current[key] = {};
        }
        current = current[key];
      }
      current[keys[keys.length - 1]] = value;

      return newData;
    });
  };

  const updateFeatureCard = (cardIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      featureCards: prev.featureCards.map((card, i) =>
        i === cardIndex ? { ...card, [field]: value } : card
      )
    }));
  };

  const addFeatureCard = () => {
    const newCard = {
      title: "",
      iconUrl: "",
      backgroundColor: "#ffffff",
      textColor: "#000000"
    };

    setFormData(prev => ({
      ...prev,
      featureCards: [...prev.featureCards, newCard]
    }));

    setTimeout(() => {
      if (lastFeatureSectionRef.current) {
        lastFeatureSectionRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const removeFeatureCard = (cardIndex) => {
    setFormData(prev => ({
      ...prev,
      featureCards: prev.featureCards.filter((_, i) => i !== cardIndex)
    }));
  };

   useEffect(() => {
      fetchOnMount();
    }, [fetchOnMount]);

    useEffect(() => {
      if (timeManagementPageData?.time_management_page) {
        // Create a deep copy to avoid mutating the read-only Redux store data
        const deepCopy = JSON.parse(JSON.stringify(timeManagementPageData.time_management_page));
        setFormData(prev => ({
          ...prev,
          ...deepCopy
        }));
      }
    }, [timeManagementPageData]);

  const handleSave = async () => {
    setLoading(true);

    try {
      // Create a FormData object for file uploads
      const formDataToSend = new FormData();

      // Append all form data
      Object.entries(formData).forEach(([key, value]) => {
        // Skip heroImage as we'll handle it separately
        if (key !== 'heroImage') {
          // Handle nested objects like primaryButton and secondaryButton
          if (typeof value === 'object' && value !== null && !(value instanceof File)) {
            formDataToSend.append(key, JSON.stringify(value));
          } else {
            formDataToSend.append(key, value);
          }
        }
      });

      // Append the hero image if it exists
      if (formData.heroImage) {
        formDataToSend.append('heroImage', formData.heroImage);
      }

      // Make sure heroImageUrl is included in the submission
      if (formData.heroImageUrl) {
        formDataToSend.append('heroImageUrl', formData.heroImageUrl);
      }

      const response = await postRequestForWebsite(
        endpoints.admin_dashboard.website?.time_management_page?.time_management_section,
        // formDataToSend,
        // dataForHeroSection,
        {
          attributes: {
            ...JSON.parse(JSON.stringify(timeManagementPageData)),
            time_management_page: formData
          }
        },
        true
      );

      if (response.metadata?.success) {
        enqueueSnackbar({
          variant: 'success',
          message: response.metadata.message,
        });
      } else {
        handleErrorMessages(response.message || 'An error occurred.');
      }
    } catch (err) {
      console.error('Error:', err);
      handleErrorMessages(err.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  };


  return (
    <Card>
      <CardContent>
        <Typography variant="h4" paragraph>
          Time Management Admin
        </Typography>

        {/* Main Header Section */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Main Header</Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Main Title"
                value={formData.mainTitle}
                onChange={(e) => updateFormData('mainTitle', e.target.value)}
                placeholder="Enter main title"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Title Color"
                type="color"
                value={formData.mainTitleColor}
                onChange={(e) => updateFormData('mainTitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>

            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Subtitle"
                multiline
                rows={2}
                value={formData.subtitle}
                onChange={(e) => updateFormData('subtitle', e.target.value)}
                placeholder="Enter subtitle"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Subtitle Color"
                type="color"
                value={formData.subtitleColor}
                onChange={(e) => updateFormData('subtitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Description Paragraphs */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Description Paragraphs</Typography>
          </Stack>

          {[1, 2, 3, 4].map((num) => (
            <Grid container spacing={3} key={num} sx={{ mb: 2 }}>
              <Grid item xs={12} md={10}>
                <TextField
                  fullWidth
                  label={`Description Paragraph ${num}`}
                  multiline
                  rows={3}
                  value={formData[`description${num}`]}
                  onChange={(e) => updateFormData(`description${num}`, e.target.value)}
                  placeholder={`Enter description paragraph ${num}`}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Text Color"
                  type="color"
                  value={formData[`description${num}Color`]}
                  onChange={(e) => updateFormData(`description${num}Color`, e.target.value)}
                  sx={{ '& input': { height: 20 } }}
                />
              </Grid>
            </Grid>
          ))}
        </Box>

        {/* Bottom CTA Section */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Bottom Call-to-Action</Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Bottom CTA Text"
                multiline
                rows={3}
                value={formData.bottomCTA}
                onChange={(e) => updateFormData('bottomCTA', e.target.value)}
                placeholder="Enter bottom call-to-action text"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Text Color"
                type="color"
                value={formData.bottomCTAColor}
                onChange={(e) => updateFormData('bottomCTAColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Text Style"
                select
                SelectProps={{ native: true }}
                value={formData.bottomCTAStyle}
                onChange={(e) => updateFormData('bottomCTAStyle', e.target.value)}
              >
                <option value="normal">Normal</option>
                <option value="italic">Italic</option>
              </TextField>
            </Grid>
          </Grid>
        </Box>

        {/* Feature Cards Section - Grid Layout */}
        <Box sx={{ mb: 5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center">
              <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
              <Typography variant="h6">Feature Cards</Typography>
            </Stack>
            <Button
              variant="contained"
              color="success"
              startIcon={<Iconify icon="mingcute:add-line" />}
              onClick={addFeatureCard}
            >
              Add Feature Card
            </Button>
          </Stack>

          <Grid container spacing={2}>
            {formData.featureCards.map((card, cardIndex) => (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={cardIndex}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, bgcolor: 'background.neutral', minHeight: 240 }}
                  ref={cardIndex === formData.featureCards.length - 1 ? lastFeatureSectionRef : null}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="caption" color="primary">
                      Feature {cardIndex + 1}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => removeFeatureCard(cardIndex)}
                        sx={{ minWidth: 25, p: 0.2 }}
                      >
                        ×
                      </Button>
                    </Stack>
                  </Stack>

                  <Stack spacing={1.5}>
                    <TextField
                      fullWidth
                      label="Feature Title"
                      size="small"
                      value={card.title}
                      onChange={(e) => updateFeatureCard(cardIndex, 'title', e.target.value)}
                      placeholder="Enter feature title"
                    />

                    <TextField
                      fullWidth
                      label="Icon URL"
                      size="small"
                      sx={{ mb: 1 }}
                      value={card.iconUrl}
                      onChange={(e) => updateFeatureCard(cardIndex, 'iconUrl', e.target.value)}
                      placeholder="Enter icon URL"
                    />

                    <TextField
                      fullWidth
                      label="Background Color"
                      type="color"
                      size="small"
                      value={card.backgroundColor}
                      onChange={(e) => updateFeatureCard(cardIndex, 'backgroundColor', e.target.value)}
                      sx={{ '& input': { height: 30, mb: 1 } }}
                    />

                    <TextField
                      fullWidth
                      label="Text Color"
                      type="color"
                      size="small"
                      value={card.textColor}
                      onChange={(e) => updateFeatureCard(cardIndex, 'textColor', e.target.value)}
                      sx={{ '& input': { height: 30 } }}
                    />

                    {/* Icon Preview */}
                    {card.iconUrl && (
                      <Box
                        sx={{
                          width: '100%',
                          height: 40,
                          bgcolor: card.backgroundColor,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden'
                        }}
                      >
                        <Box
                          component="img"
                          src={card.iconUrl}
                          alt={`${card.title} Icon`}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                          sx={{
                            width: 24,
                            height: 24,
                            objectFit: 'contain',
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            position: 'absolute',
                            display: 'none',
                            color: 'text.disabled',
                            fontSize: 10
                          }}
                        >
                          Invalid URL
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Action Buttons */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={2}
          sx={{ pt: 3, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <LoadingButton
            loading={loading}
            variant="contained"
            color="primary"
            onClick={handleSave}
            startIcon={<Iconify icon="eva:save-fill" />}
            size="large"
          >
            Update
          </LoadingButton>
        </Stack>
      </CardContent>

    </Card>
  );
}
